import { useEffect, useMemo, useState } from 'react';
import { formatUsd } from '../../lib/format';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';

/**
 * SIL-2062: per-SKU asking price per unit for a lot. One row per SKU group in the
 * lot (SKU, units, FMV/unit, asking/unit input, line total) and the derived lot
 * total. The same table creates the listing (no listing yet) and edits an
 * already-listed lot (draft | active, no open offer); the database derives and
 * enforces the lot total, this card only previews it.
 */

export interface LotSkuGroup {
  skuId: string | null;
  skuLabel: string | null;
  unitCount: number;
  fmvPerUnitUsd: number | null;
}

export interface LotOpenListing {
  id: string;
  status: string;
  totalUsd: number | null;
  unitPrices: Array<{ skuId: string; unitPriceUsd: number }>;
}

export interface LotUnitPricingCardProps {
  skuGroups: LotSkuGroup[];
  listing: LotOpenListing | null;
  /** Every member is in a listable lifecycle state (only matters before listing). */
  listable: boolean;
  busy: boolean;
  /** Create the listing priced per unit. Must reject with an Error to keep the card open. */
  onList: (prices: Array<{ skuId: string; unitPriceUsd: number }>) => Promise<void>;
  /** Re-price the already-listed lot. */
  onSave: (prices: Array<{ skuId: string; unitPriceUsd: number }>) => Promise<void>;
}

/** Cents-exact money maths: unit prices have at most 2 decimals. */
function toCents(n: number): number {
  return Math.round(n * 100);
}

/** A cell value as a price: a positive number with at most 2 decimals, else null. */
export function parseUnitPrice(raw: string): number | null {
  const t = raw.trim();
  if (t === '' || !/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return n > 0 ? n : null;
}

const EDITABLE_STATUSES = new Set(['draft', 'active']);

export function LotUnitPricingCard({ skuGroups, listing, listable, busy, onList, onSave }: LotUnitPricingCardProps) {
  const unpriceable = skuGroups.filter((g) => g.skuId === null);
  const groups = skuGroups.filter((g): g is LotSkuGroup & { skuId: string } => g.skuId !== null);

  // Seed from the listing's current prices; re-seed whenever they change server-side.
  const seed = useMemo(() => {
    const m: Record<string, string> = {};
    for (const u of listing?.unitPrices ?? []) m[u.skuId] = String(u.unitPriceUsd);
    return m;
  }, [listing]);
  const seedKey = JSON.stringify(seed);
  const [values, setValues] = useState<Record<string, string>>(seed);
  useEffect(() => setValues(seed), [seedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (groups.length === 0 && unpriceable.length === 0) return null;

  const parsed = groups.map((g) => ({ g, price: parseUnitPrice(values[g.skuId] ?? '') }));
  const allPriced = unpriceable.length === 0 && parsed.length > 0 && parsed.every((p) => p.price !== null);
  const totalCents = allPriced
    ? parsed.reduce((sum, p) => sum + toCents(p.price as number) * p.g.unitCount, 0)
    : null;
  const total = totalCents === null ? null : totalCents / 100;

  const listed = listing !== null;
  const editable = !listed || EDITABLE_STATUSES.has(listing.status);
  const changed = listed
    ? parsed.some((p) => p.price !== (listing.unitPrices.find((u) => u.skuId === p.g.skuId)?.unitPriceUsd ?? null))
    : true;
  const payload = () => parsed.map((p) => ({ skuId: p.g.skuId, unitPriceUsd: p.price as number }));

  return (
    <Card data-testid="lot-unit-pricing">
      <PanelTitle
        title="Price per unit"
        description="Set an asking price per unit for each SKU in this lot. The lot total is the sum of unit price × units."
      />

      {unpriceable.length > 0 ? (
        <p className="mt-3 text-sm text-amber-700" data-testid="lot-unit-pricing-unpriceable">
          {unpriceable.reduce((n, g) => n + g.unitCount, 0)} asset(s) in this lot have no matched SKU, so the lot cannot be
          priced per unit. Match them to a SKU, or list the lot at a flat total.
        </p>
      ) : (
        <>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">SKU</th>
                  <th className="py-1 pr-3 text-right font-medium">Qty</th>
                  <th className="py-1 pr-3 text-right font-medium">FMV / unit</th>
                  <th className="py-1 pr-3 text-right font-medium">Asking / unit</th>
                  <th className="py-1 text-right font-medium">Line total</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map(({ g, price }) => (
                  <tr key={g.skuId} className="border-t border-border" data-testid={`unit-row-${g.skuId}`}>
                    <td className="py-1.5 pr-3">{g.skuLabel ?? g.skuId}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{g.unitCount}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{formatUsd(g.fmvPerUnitUsd)}</td>
                    <td className="py-1.5 pr-3 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={values[g.skuId] ?? ''}
                        disabled={busy || !editable}
                        onChange={(e) => setValues((v) => ({ ...v, [g.skuId]: e.target.value }))}
                        placeholder="0.00"
                        aria-label={`Asking price per unit for ${g.skuLabel ?? g.skuId}`}
                        aria-invalid={(values[g.skuId] ?? '') !== '' && price === null}
                        className="w-32 rounded-md border border-border px-2 py-1 text-right text-sm tabular-nums"
                      />
                    </td>
                    <td className="py-1.5 text-right tabular-nums" data-testid={`unit-line-${g.skuId}`}>
                      {price === null ? '—' : formatUsd((toCents(price) * g.unitCount) / 100)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border font-medium">
                  <td className="py-1.5 pr-3" colSpan={4}>
                    Lot total
                  </td>
                  <td className="py-1.5 text-right tabular-nums" data-testid="unit-lot-total">
                    {formatUsd(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {listed && !editable && (
            <p className="mt-2 text-xs text-amber-700">
              Prices cannot be changed while the listing is {listing.status.replace('_', ' ')}.
            </p>
          )}

          <div className="mt-3 flex justify-end">
            {listed ? (
              <Button
                size="sm"
                disabled={busy || !editable || !allPriced || !changed}
                onClick={() => void onSave(payload())}
              >
                Save prices
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={busy || !listable || !allPriced}
                title={!listable ? 'Every member must be in a listable state' : !allPriced ? 'Enter a price for every SKU' : ''}
                onClick={() => void onList(payload())}
              >
                List at these prices
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
